import { getInactiveDriversOptions, getAllDriversOptions, getExternalDriversOptions } from '@/api/main'
import { DriverResponse, ExternalDriverModel } from '@/api/main/types.gen'
import { UnifiedDriver } from '@/types/driver'
import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

function useDriverData() {
  const { inactiveDrivers, allDrivers, externalDrivers, isLoading } = useQueries({
    queries: [getInactiveDriversOptions(), getAllDriversOptions(), getExternalDriversOptions()],
    combine: results => ({
      inactiveDrivers: results[0].data,
      allDrivers: results[1].data,
      externalDrivers: results[2].data,
      isLoading: results.some(result => result.isLoading),
      isError: results.some(result => result.isError),
      error: results.find(result => result.error)?.error || null,
    }),
  })

  const unifiedDrivers = useMemo(() => {
    const drivers: UnifiedDriver[] = []

    const internalDriversMap = new Map<number, DriverResponse>()
    const driversByInternalId = new Map<number, UnifiedDriver>()

    const allInternalDrivers = [
      ...(Array.isArray(allDrivers) ? allDrivers : []),
      ...(Array.isArray(inactiveDrivers) ? inactiveDrivers : []),
    ]

    allInternalDrivers.forEach((driver: DriverResponse) => {
      internalDriversMap.set(driver.id, driver)
      const unifiedDriver: UnifiedDriver = {
        id: driver.id,
        name: driver.name,
        phone: driver.phone,
        email: driver.email,
        active: driver.active,
        internalId: driver.id,
        canLink: false,
        canImport: false,
        type: 'internal',
      }
      drivers.push(unifiedDriver)
      driversByInternalId.set(driver.id, unifiedDriver)
    })

    if (externalDrivers?.external_drivers && Array.isArray(externalDrivers.external_drivers)) {
      externalDrivers.external_drivers.forEach((externalDriver: ExternalDriverModel) => {
        const linkedInternalDriver = externalDriver.driver_id
          ? externalDrivers.drivers?.[externalDriver.driver_id.toString()]
          : null

        if (linkedInternalDriver) {
          const existingEntry = driversByInternalId.get(linkedInternalDriver.id)
          if (existingEntry) {
            existingEntry.externalId = externalDriver.external_id
            existingEntry.canLink = false
            existingEntry.canImport = false
            existingEntry.type = 'linked'
          }
        } else {
          drivers.push({
            id: externalDriver.external_id,
            name: externalDriver.imported_name,
            externalId: externalDriver.external_id,
            canLink: true,
            canImport: true,
            type: 'external',
          })
        }
      })
    }

    return drivers
  }, [allDrivers, externalDrivers, inactiveDrivers])

  return {
    inactiveDrivers,
    allDrivers,
    externalDrivers,
    isLoading,
    unifiedDrivers,
  }
}

export default useDriverData
